import { TrialModal } from 'features/forceTrial/TrialModal';
import { useHasTrialModalBeenClosedSomeTimeAgo } from 'features/forceTrial/useHasTrialModalBeenClosedSomeTimeAgo';
import {
  useIsSubscriptionSetToFree,
  useRemoveSubscriptionSetToFree
} from 'features/forceTrial/useIsSubscriptionSetToFree';
import { useSetModalIsClosedInLocalStorage } from 'features/forceTrial/useSetModalIsClosedInLocalStorage';
import useSubscriptionState from 'features/pricing/hook/useSubscriptionState';
import { recentPaymentWasSuccessful } from 'pages/paymentCallback/PaymentCallbackPage';
import { ReactElement, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useAppSelector } from 'store/hooks';

const trialModalExcludedPages = [
  '/onboarding',
  '/login',
  '/free',
  '/payment-success',
  '/payment-callback',
  '/register',
  '/reset-password',
  // This pages will show a modal and only once this modal has been completed,
  // the user is redirected to a document.
  // Therefore blocking it here too.
  '/aiWriter/new'
];

/**
 * This is a special modal, showing under certain conditions no matter
 * where the user is in the app.
 */
export const TrialModalLoader = (): ReactElement | null => {
  const enforceFreeTrial = useAppSelector(state => state.customer.enforceFreeTrial);
  const trialUsed = useAppSelector(state => state.customer.trialUsed);
  const preferredPricingId = useAppSelector(state => state.customer.preferredPricingId);

  const customerId = useAppSelector(state => state.customer.id);
  const { currentPricingPlan, currentSubscriptionState } = useSubscriptionState();

  const [localModalClosed, setLocalModalClosed] = useState(false);

  const isSubscriptionSetToFree = useIsSubscriptionSetToFree();

  const trialModalClosedTimer = useHasTrialModalBeenClosedSomeTimeAgo();
  const setModalIsClosedInLocalStorage = useSetModalIsClosedInLocalStorage();

  const removeSubscriptionSetToFree = useRemoveSubscriptionSetToFree();

  const location = useLocation();

  useEffect(() => {
    if (localModalClosed && preferredPricingId) {
      setModalIsClosedInLocalStorage();
    }
  }, [localModalClosed, preferredPricingId, setModalIsClosedInLocalStorage]);

  // Hide this modal when customer is not authenticated
  if (!customerId) {
    return null;
  }

  // Prevent race condition if the payment is not processed fast enough
  if (recentPaymentWasSuccessful()) {
    return null;
  }

  // Hide this modal on specific pages
  if (trialModalExcludedPages.findIndex(path => location.pathname.startsWith(path)) > -1) {
    return null;
  }

  // While we are still loading the subscription state, no modal should be displayed
  if (!currentSubscriptionState) {
    return null;
  }

  // If the customer already has a paid subscription, we don't need to ask him to create one
  if (
    currentPricingPlan.type !== 'free' &&
    (currentSubscriptionState?.status === 'subscribed' ||
      currentSubscriptionState?.status === 'grace' ||
      currentSubscriptionState?.status === 'on trial' ||
      currentSubscriptionState?.status === 'on trial grace')
  ) {
    return null;
  }

  // If the trial has already been used, we don't want to offer it again
  if (trialUsed && !isSubscriptionSetToFree) {
    return null;
  }

  // If the optional modal has already been closed before, the customer does not see it again for a certain timeframe
  if (
    !enforceFreeTrial &&
    trialModalClosedTimer === 'should_not_show' &&
    !isSubscriptionSetToFree
  ) {
    return null;
  }

  if (localModalClosed) {
    return null;
  }

  const handleModalSuccessfulClose = () => {
    setLocalModalClosed(true);
    setModalIsClosedInLocalStorage();
  };

  const handleModalFailedClose = () => {
    removeSubscriptionSetToFree();
    setLocalModalClosed(true);
  };

  const handleModalTemporaryClose = () => {
    setLocalModalClosed(!enforceFreeTrial);
  };

  return (
    <TrialModal
      isSubscriptionSetToFree={isSubscriptionSetToFree}
      trialModalClosedTimer={trialModalClosedTimer}
      onModalSuccessfulClose={handleModalSuccessfulClose}
      onModalFailClose={handleModalFailedClose}
      onModalTemporaryClose={handleModalTemporaryClose}
    />
  );
};
